import { Link } from "react-router-dom";
import { Container, Section, Eyebrow, Display, SEO } from "./ui";
import { canonicalFor } from "../routes";

function PrivacyPolicy() {
  return (
    <>
      <SEO
        title="Privacy policy — WhoBrew"
        description="How WhoBrew handles your data and your card visitors' privacy."
        canonical={canonicalFor("/privacy")}
      />
    <Section tone="page" spacing="loose">
      <Container width="prose">
        <Eyebrow>Privacy</Eyebrow>
        <Display as="h1" scale="lg" italicWord="policy" className="mt-5">
          Privacy policy.
        </Display>
        <p className="mt-6 font-mono text-caption text-coffee-600 dark:text-cream-200 dark:text-cream-200 mb-12">Last updated: April 2026</p>

        <div className="space-y-6 text-coffee-700 dark:text-cream-200 leading-relaxed">

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">1. Introduction</h2>
          <p>
            At WhoBrew, we take your privacy seriously. This Privacy Policy explains what
            personal information we collect when you use our NFC business card service, how we
            use that information, and the choices you have regarding your data. By using our
            website and services, you agree to the practices described in this policy.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">
            2. Information we collect
          </h2>
          <p>
            We collect the following types of information to provide and improve our services:
          </p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Name</strong> — to personalize your account
              and card profiles
            </li>
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Email address</strong> — for account access,
              order confirmations, and service communications
            </li>
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Phone number</strong> — optionally provided
              for your card profile or account recovery
            </li>
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Shipping address</strong> — to deliver your
              physical NFC cards
            </li>
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Card profile data</strong> — the information
              you choose to display on your digital business card, such as job title, company,
              social links, and bio
            </li>
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Usage analytics</strong> — aggregated data
              about how your cards are tapped and viewed, used to power your analytics dashboard
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">
            3. How we use your information
          </h2>
          <p>We use the information we collect for the following purposes:</p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>To provide, maintain, and improve our NFC business card services</li>
            <li>To process and fulfill your card orders and subscriptions</li>
            <li>To improve our products, features, and user experience</li>
            <li>
              To send you relevant updates about your account, orders, and service changes
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">
            4. Information sharing
          </h2>
          <p>
            We do not sell your personal data to third parties. We only share your information
            in the following limited circumstances:
          </p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Shipping providers</strong> — we share your
              name and shipping address with our fulfillment partners to deliver your NFC cards
            </li>
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Payment processors</strong> — payment
              information is handled securely by our third-party payment processor to complete
              transactions
            </li>
          </ul>
          <p>
            These partners are contractually obligated to use your data only to perform services
            on our behalf and to protect your information.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">5. Data security</h2>
          <p>
            We employ industry-standard security measures to protect your personal information.
            All data is transmitted using SSL/TLS encryption and stored in secure,
            access-controlled databases. While no method of transmission or storage is
            completely secure, we are committed to safeguarding your data using best practices
            and regularly reviewing our security procedures.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">6. Your rights</h2>
          <p>You have the right to:</p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Access</strong> your personal data that we
              hold
            </li>
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Update</strong> your information at any time
              through your account settings
            </li>
            <li>
              <strong className="text-coffee-900 dark:text-cream-50">Delete</strong> your account and associated
              data
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{" "}
            <a
              href="mailto:support@whobrew.com"
              className="text-coffee-700 dark:text-cream-200 underline decoration-coffee-300 hover:text-coffee-900 transition-colors"
            >
              support@whobrew.com
            </a>
            . We will respond to your request within 30 days.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">7. Cookies</h2>
          <p>
            We use basic analytics cookies to understand how visitors interact with our website
            and to improve the user experience. These cookies collect aggregated, anonymous data
            such as page views and session duration. We do not use cookies to track you across
            other websites or for targeted advertising.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">
            8. Children's privacy
          </h2>
          <p>
            Our service is intended for users who are 18 years of age or older. We do not
            knowingly collect personal information from individuals under 18. If we become aware
            that we have inadvertently collected data from someone under 18, we will take steps
            to delete that information promptly.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">
            9. Changes to this policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our
            practices or for legal and regulatory reasons. When we make significant changes, we
            will notify you via the email address associated with your account. We encourage you
            to review this page periodically for the latest information.
          </p>

          <h2 className="text-xl font-semibold text-coffee-900 dark:text-cream-50 mt-10 mb-3">10. Contact us</h2>
          <p>
            If you have any questions or concerns about this Privacy Policy or our data
            practices, please reach out to us at{" "}
            <a
              href="mailto:support@whobrew.com"
              className="text-coffee-700 dark:text-cream-200 underline decoration-coffee-300 hover:text-coffee-900 transition-colors"
            >
              support@whobrew.com
            </a>
            .
          </p>

          <div className="mt-10 pt-8 border-t border-coffee-100 dark:border-coffee-800">
            <Link
              to="/"
              className="text-coffee-700 dark:text-cream-200 underline decoration-coffee-300 hover:text-coffee-900 font-medium transition-colors"
            >
              &larr; Back to home
            </Link>
          </div>

        </div>
      </Container>
    </Section>
    </>
  );
}

export default PrivacyPolicy;
